import { z } from 'zod';

export const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required').max(128),
});

export type LoginInput = z.infer<typeof loginSchema>;

const emailField = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, 'Email is required')
  .max(200)
  .email('Please enter a valid email address');

// Password complexity rules. Intentionally duplicated from user.schema's
// `passwordSchema` rather than imported — user.schema pulls in
// libphonenumber-js, and this module is imported by the (client) login form,
// so importing it would bloat the login bundle.
const passwordComplexity = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be at most 128 characters')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one symbol');

export const requestPasswordResetSchema = z.object({
  email: emailField,
});

export type RequestPasswordResetInput = z.infer<
  typeof requestPasswordResetSchema
>;

export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, 'Reset token is required').max(512),
    password: passwordComplexity,
    confirmPassword: z.string().min(1, 'Please confirm your password').max(128),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// Client-side form schema for the reset page (token comes from the URL, not the
// form). Same refine as the full schema.
export const resetPasswordFormSchema = z
  .object({
    password: passwordComplexity,
    confirmPassword: z.string().min(1, 'Please confirm your password').max(128),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type ResetPasswordFormInput = z.infer<typeof resetPasswordFormSchema>;
